import { Injectable } from '@angular/core';
import {
  ClinicalDiagnosisDto, ConsultationAttachmentDto, ConsultationDischargeDto,
  ConsultationLaboratoryAnalysisDto, ConsultationMedicineDto, ConsultationOverviewDto,
  ConsultationSectionDtoMap, ConsultationSummaryDto, ConsultationSupplyDto,
  PatientConsultationDetailDto, PatientConsultationNoteDto, PatientDewormingDto, PatientPrescriptionDto,
  PatientPrescriptionItemDto, PatientTreatmentDto, PatientTreatmentPlanDto, PatientVaccinationDto,
  PatientVitalsSnapshotDto,
} from '../dtos/clinical-history.dto';
import {
  ConsultationAttachmentFile, ConsultationDetail, ConsultationDischarge,
  ConsultationMedicine, ConsultationNoteEntry, ConsultationOverview, ConsultationSectionDataMap,
  ConsultationSectionKey, ConsultationSummary, Deworming, Diagnosis, LaboratoryAnalysis, Prescription,
  PrescriptionItem, Supply, Treatment, TreatmentPlan, Vaccination, VitalsSnapshot,
} from '../../domain/models/clinical-history.model';

// Per-section mapping table type: indexing it with the section literal a caller passes is what
// keeps `ClinicalHistoryMapper.toConsultationSection` type-safe without a cast, since both the
// argument and the return type resolve from the same key.
type ConsultationSectionMappers = {
  [K in ConsultationSectionKey]: (dto: ConsultationSectionDtoMap[K]) => ConsultationSectionDataMap[K];
};

const TREATMENT_PLAN_ITEM_KINDS = ['medicine', 'food', 'procedure', 'supply'] as const;

function isTreatmentPlanItemKind(value: string | null): value is TreatmentPlan['itemKind'] & string {
  return value !== null && (TREATMENT_PLAN_ITEM_KINDS as readonly string[]).includes(value);
}

// `Notes` is the source of truth whenever the response carries it -- including when it is empty,
// which is an authoritative "no note with text" rather than a reason to retry on the raw rows.
// `NotesRaw` is only parsed when the key is absent altogether, i.e. against a backend deployed
// before the typed projection existed; that path can resolve no author or type, so both stay null.
function toConsultationNotes(dto: PatientConsultationDetailDto): readonly ConsultationNoteEntry[] {
  if (dto.Notes) return dto.Notes.map(note => toNoteEntry(note));

  return extractRawNoteEntries(dto.NotesRaw ?? []);
}

function toNoteEntry(dto: PatientConsultationNoteDto): ConsultationNoteEntry {
  return {
    text: dto.Text,
    date: dto.DateAdded ?? null,
    typeLabel: dto.TypeLabel ?? null,
    authorName: dto.AuthorName ?? null,
  };
}

function toAttachmentFile(dto: ConsultationAttachmentDto): ConsultationAttachmentFile {
  return {
    noteId: dto.NoteId,
    uploadId: dto.UploadId,
    fileName: dto.FileName,
    sizeBytes: dto.SizeBytes,
    contentType: dto.ContentType,
  };
}

// A raw `consultationnote` row has no confirmed schema, so only surface an entry when one of these
// plausible keys resolves to a non-empty string -- never dump the raw JSON object into the UI. Same
// filter the backend's own `BuildConsultationNotes` applies, so both paths keep and drop the same
// rows. Twin of `features/clients`' copy in its own `clinical-history.mapper.ts`; both go away with
// `NotesRaw` itself.
function extractRawNoteEntries(raw: readonly Record<string, unknown>[]): ConsultationNoteEntry[] {
  const entries: ConsultationNoteEntry[] = [];

  for (const entry of raw) {
    for (const key of ['text', 'note']) {
      const value = entry[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        const dateAdded = entry['date_added'];
        entries.push({
          text: value,
          date: typeof dateAdded === 'string' && dateAdded.trim().length > 0 ? dateAdded : null,
          typeLabel: null,
          authorName: null,
        });
        break;
      }
    }
  }

  return entries;
}

@Injectable()
export class ClinicalHistoryMapper {
  public toDiagnosis(dto: ClinicalDiagnosisDto): Diagnosis {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId ?? null,
      code: dto.Code ?? null,
      name: dto.Name ?? null,
      date: dto.Date ?? null,
      description: dto.Description ?? null,
      category: dto.Category ?? null,
      type: dto.Type ?? null,
      veterinarianName: dto.SupervisingVeterinarianName ?? null,
    };
  }

  public toVaccination(dto: PatientVaccinationDto): Vaccination {
    return {
      id: dto.ExternalId,
      name: dto.Name ?? null,
      diseases: dto.VaccinationDisease,
      givenDate: dto.GivenDate ?? null,
      expiryDate: dto.ExpiryDate ?? null,
    };
  }

  public toTreatment(dto: PatientTreatmentDto): Treatment {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId ?? null,
      name: dto.Name ?? null,
      instructions: dto.Instructions ?? null,
      used: dto.Used ?? null,
      quantity: dto.Quantity ?? null,
      unit: dto.Unit ?? null,
      veterinarianName: dto.SupervisingVeterinarianName ?? null,
      administrationMethod: dto.AdministrationMethod ?? null,
      typeCode: dto.TypeCode ?? null,
      typeLabel: dto.TypeLabel ?? null,
    };
  }

  public toSupply(dto: ConsultationSupplyDto): Supply {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId ?? null,
      patientId: dto.PatientId ?? null,
      name: dto.Name ?? null,
      instructions: dto.Instructions ?? null,
      used: dto.Used ?? null,
      quantity: dto.Quantity ?? null,
      unit: dto.Unit ?? null,
      packageDescription: dto.PackageDescription ?? null,
      usageSize: dto.UsageSize ?? null,
      supervisingVeterinarianId: dto.SupervisingVeterinarianId ?? null,
      supervisingVeterinarianName: dto.SupervisingVeterinarianName ?? null,
      performedById: dto.PerformedById ?? null,
      performedByName: dto.PerformedByName ?? null,
    };
  }

  public toLaboratoryAnalysis(dto: ConsultationLaboratoryAnalysisDto): LaboratoryAnalysis {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId ?? null,
      patientId: dto.PatientId ?? null,
      name: dto.Name ?? null,
      code: dto.Code ?? null,
      used: dto.Used ?? null,
      result: dto.Result ?? null,
      resultNumeric: dto.ResultNumeric ?? null,
      resultEntered: dto.ResultEntered ?? null,
      referenceLow: dto.ReferenceLow ?? null,
      referenceHigh: dto.ReferenceHigh ?? null,
      measurementUnit: dto.MeasurementUnit ?? null,
      remarks: dto.Remarks ?? null,
      approved: dto.Approved ?? null,
      approvedDate: dto.ApprovedDate ?? null,
      supervisingVeterinarianId: dto.SupervisingVeterinarianId ?? null,
      supervisingVeterinarianName: dto.SupervisingVeterinarianName ?? null,
      performedById: dto.PerformedById ?? null,
      performedByName: dto.PerformedByName ?? null,
      referralId: dto.ReferralId ?? null,
      device: dto.Device ?? null,
    };
  }

  // Same per-item mapping as the `attachments` section entry in `toConsultationSection` below
  // (both back onto the module-level `toAttachmentFile`), exposed as its own method because the
  // lab-request drawer's attachment lookup is a standalone endpoint, not a `ConsultationSectionKey`.
  public toConsultationAttachmentFiles(
    dtos: readonly ConsultationAttachmentDto[],
  ): readonly ConsultationAttachmentFile[] {
    return dtos.map(dto => toAttachmentFile(dto));
  }

  public toDeworming(dto: PatientDewormingDto): Deworming {
    return {
      id: dto.ExternalId,
      name: dto.Name ?? null,
      description: dto.Description ?? null,
      date: dto.Date ?? null,
    };
  }

  public toTreatmentPlan(dto: PatientTreatmentPlanDto): TreatmentPlan {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId,
      startDate: dto.StartDate ?? null,
      endDate: dto.EndDate ?? null,
      ongoing: dto.Ongoing ?? null,
      timings: dto.Timings ?? null,
      everyNDays: dto.EveryNDays ?? null,
      repetitionsPerDay: dto.RepetitionsPerDay ?? null,
      isCabinetItem: dto.IsCabinetItem ?? null,
      doseCount: dto.DoseCount ?? null,
      itemKind: isTreatmentPlanItemKind(dto.ItemKind) ? dto.ItemKind : null,
      itemName: dto.ItemName ?? null,
      itemCode: dto.ItemCode ?? null,
      quantity: dto.Quantity ?? null,
      unit: dto.Unit ?? null,
      packageDescription: dto.PackageDescription ?? null,
      usageSize: dto.UsageSize ?? null,
      instructions: dto.Instructions ?? null,
    };
  }

  public toConsultationMedicine(dto: ConsultationMedicineDto): ConsultationMedicine {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId ?? null,
      patientId: dto.PatientId ?? null,
      vaccination: dto.Vaccination,
      name: dto.Name ?? null,
      used: dto.Used ?? null,
      quantity: dto.Quantity ?? null,
      unit: dto.Unit ?? null,
      administrationMethodCode: dto.AdministrationMethodCode ?? null,
      administrationMethodLabel: dto.AdministrationMethodLabel ?? null,
      vaccinationTypeLabel: dto.VaccinationTypeLabel ?? null,
      vaccinationTypeCode: dto.VaccinationTypeCode ?? null,
      activeSubstance: dto.ActiveSubstance ?? null,
      manufacturerData: dto.ManufacturerData ?? null,
      preparation: dto.Preparation ?? null,
      contraindications: dto.Contraindications ?? null,
      instructions: dto.Instructions ?? null,
      withdrawalPeriods: dto.WithdrawalPeriods ?? null,
      veterinarianId: dto.SupervisingVeterinarianId ?? null,
      veterinarianName: dto.SupervisingVeterinarianName ?? null,
      medicationDiagnosisId: dto.MedicationDiagnosisId ?? null,
      invoiceRowId: dto.InvoiceRowId ?? null,
      performedById: dto.PerformedById ?? null,
      performedByName: dto.PerformedByName ?? null,
      usageTypeCode: dto.UsageTypeCode ?? null,
      usageTypeLabel: dto.UsageTypeLabel ?? null,
      administrationHours: dto.AdministrationHours ?? null,
      administrationSpeed: dto.AdministrationSpeed ?? null,
      medicationDurationDays: dto.MedicationDurationDays ?? null,
      controlledSubstance: dto.ControlledSubstance ?? null,
    };
  }

  public toVitalsSnapshot(dto: PatientVitalsSnapshotDto): VitalsSnapshot {
    return {
      text: dto.Text ?? null,
      weight: dto.Weight ?? null,
      weightTimestamp: dto.WeightTimestamp ?? null,
      weightUnits: dto.WeightUnits ?? null,
      temperature: dto.Temperature ?? null,
      temperatureTimestamp: dto.TemperatureTimestamp ?? null,
      temperatureUnits: dto.TemperatureUnits ?? null,
      heartRate: dto.HeartRate ?? null,
      heartRateTimestamp: dto.HeartRateTimestamp ?? null,
      respiratoryRate: dto.RespiratoryRate ?? null,
      respiratoryRateTimestamp: dto.RespiratoryRateTimestamp ?? null,
      bodyConditionScore: dto.BodyConditionScore ?? null,
      bodyConditionScoreTimestamp: dto.BodyConditionScoreTimestamp ?? null,
      bloodPressure: dto.BloodPressure ?? null,
      bloodPressureTimestamp: dto.BloodPressureTimestamp ?? null,
      bloodPressureBodyPositionLabel: dto.BloodPressureBodyPositionLabel ?? null,
      bloodPressureBodyPositionCode: dto.BloodPressureBodyPositionCode ?? null,
      peripheralOxygenSaturation: dto.PeripheralOxygenSaturation ?? null,
      peripheralOxygenSaturationTimestamp: dto.PeripheralOxygenSaturationTimestamp ?? null,
      bloodGlucose: dto.BloodGlucose ?? null,
      centralVenousPressure: dto.CentralVenousPressure ?? null,
    };
  }

  public toConsultationDetail(dto: PatientConsultationDetailDto): ConsultationDetail {
    return {
      id: dto.ExternalId,
      complaint: dto.Complaint ?? null,
      admittedTime: dto.AdmittedTime ?? null,
      started: dto.Started ?? null,
      finished: dto.Finished ?? null,
      ward: dto.Ward ?? null,
      patientLocation: dto.PatientLocation ?? null,
      triage: dto.Triage ?? null,
      holdingPlaceNumber: dto.HoldingPlaceNumber ?? null,
      referringOrganization: dto.ReferringOrganization ?? null,
      referringVeterinarian: dto.ReferringVeterinarian ?? null,
      integrationStatus: dto.IntegrationStatus ?? null,
      externalSent: dto.ExternalSent ?? null,
      reportToAuthorities: dto.ReportToAuthorities ?? null,
      vitals: dto.Vitals ? this.toVitalsSnapshot(dto.Vitals) : null,
      dischargeInstructionsText: dto.DischargeInstructionsText ?? null,
      dischargeInstructionsPrintedAt: dto.DischargeInstructionsPrintedAt ?? null,
      notes: toConsultationNotes(dto),
      diagnoses: dto.Diagnoses.map(item => this.toDiagnosis(item)),
      items: dto.Items.map(item => this.toConsultationMedicine(item)),
      prescriptions: (dto.Prescriptions ?? []).map(item => this.toPrescription(item)),
      attachmentFiles: (dto.Attachments ?? []).map(attachment => toAttachmentFile(attachment)),
    };
  }

  public toConsultationSummary(dto: ConsultationSummaryDto): ConsultationSummary {
    return {
      id: dto.ExternalId,
      title: dto.Title ?? null,
      date: dto.Date ?? null,
      typeLabel: dto.TypeLabel ?? null,
      patientId: dto.PatientId ?? null,
    };
  }

  public toConsultationOverview(dto: ConsultationOverviewDto): ConsultationOverview {
    return {
      id: dto.ExternalId,
      complaint: dto.Complaint ?? null,
      statusCode: dto.Status ?? null,
      statusLabel: dto.StatusLabel ?? null,
      typeLabel: dto.TypeLabel ?? null,
      admittedTime: dto.AdmittedTime ?? null,
      started: dto.Started ?? null,
      finished: dto.Finished ?? null,
      ward: dto.Ward ?? null,
      patientLocation: dto.PatientLocation ?? null,
      triage: dto.Triage ?? null,
      veterinarianName: dto.VeterinarianName ?? null,
      departmentName: dto.DepartmentName ?? null,
      invoice: dto.InvoiceId ? {
        id: dto.InvoiceId,
        total: dto.InvoiceTotal ?? null,
        isPending: dto.InvoiceIsPending ?? false,
      } : null,
      notesCount: dto.NotesCount ?? null,
      diagnosesCount: dto.DiagnosesCount,
      dischargeCount: dto.DischargeCount ?? null,
      hasVitalsSnapshot: dto.HasVitalsSnapshot,
      hasConsultationItems: dto.HasConsultationItems,
      hasTreatmentPlans: dto.HasTreatmentPlans,
    };
  }

  public toConsultationDischarge(dto: ConsultationDischargeDto): ConsultationDischarge {
    return {
      ...(dto.InstructionCount == null ? {} : { instructionCount: dto.InstructionCount }),
      text: dto.Text ?? null,
      printedAt: dto.PrintedAt ?? null,
    };
  }

  // One entry point for all 13 sections: the caller passes the same literal the URL carries and
  // gets that section's mapped payload back, with no cast anywhere -- see
  // `ConsultationSectionMappers`.
  public toConsultationSection<K extends ConsultationSectionKey>(
    section: K, dto: ConsultationSectionDtoMap[K],
  ): ConsultationSectionDataMap[K] {
    const mappers: ConsultationSectionMappers = {
      overview: value => this.toConsultationOverview(value),
      vitals: value => (value ? this.toVitalsSnapshot(value) : null),
      diagnoses: value => value.map(item => this.toDiagnosis(item)),
      medicines: value => value.map(item => this.toConsultationMedicine(item)),
      procedures: value => value.map(item => this.toTreatment(item)),
      supplies: value => value.map(item => this.toSupply(item)),
      analysis: value => value.map(item => this.toLaboratoryAnalysis(item)),
      plans: value => value.map(item => this.toTreatmentPlan(item)),
      prescriptions: value => value.map(item => this.toPrescription(item)),
      notes: value => value.map(item => toNoteEntry(item)),
      discharge: value => this.toConsultationDischarge(value),
      attachments: value => value.map(item => toAttachmentFile(item)),
    };

    return mappers[section](dto);
  }

  public toPrescription(dto: PatientPrescriptionDto): Prescription {
    return {
      id: dto.ExternalId,
      consultationId: dto.ConsultationId ?? null,
      patientId: dto.PatientId ?? null,
      patientName: null,
      prescriberName: dto.PrescriberName ?? null,
      issueTime: dto.IssueTime ?? null,
      validMonths: dto.ValidMonths ?? null,
      validDays: dto.ValidDays ?? null,
      controlled: dto.Controlled ?? null,
      microbial: dto.Microbial ?? null,
      diagnosis: dto.Diagnosis ?? null,
      items: dto.Items.map(item => this.toPrescriptionItem(item)),
      documentId: dto.DocumentId ?? null,
      sentAtUtc: dto.SentAtUtc ?? null,
    };
  }

  private toPrescriptionItem(dto: PatientPrescriptionItemDto): PrescriptionItem {
    return {
      id: dto.ExternalId,
      code: dto.Code ?? null,
      name: dto.Name ?? null,
      quantity: dto.Quantity ?? null,
      instructions: dto.Instructions ?? null,
      units: dto.Units ?? null,
      unit: dto.Unit ?? null,
      packageDescription: dto.PackageDescription ?? null,
      usageSize: dto.UsageSize ?? null,
      dosageUnits: dto.DosageUnits ?? null,
      withdrawalPeriods: dto.WithdrawalPeriods ?? null,
      refills: dto.Refills ?? null,
      dosageForm: dto.DosageForm ?? null,
    };
  }
}
